import type { Messages } from "../messages";
import { plural } from "../types";

// Tuteo throughout, and vocabulary kept neutral between Spain and Latin
// America where the two diverge — "el signo #" rather than "almohadilla".
export const es: Messages = {
  common: {
    copy: "Copiar",
    copied: "Copiado",
    optional: "Opcional",
    show: "Mostrar",
    hide: "Ocultar",
    loading: "Cargando…",
    on: "Activado",
    off: "Desactivado",
  },

  language: {
    label: "Idioma",
  },

  header: {
    encrypted: "cifrado de extremo a extremo",
  },

  bytes: {
    b: "{size} B",
    kb: "{size} KB",
    mb: "{size} MB",
    gb: "{size} GB",
  },

  duration: {
    standalone: {
      hours: plural({ one: "{count} hora", other: "{count} horas" }),
      days: plural({ one: "{count} día", other: "{count} días" }),
      weeks: plural({ one: "{count} semana", other: "{count} semanas" }),
    },
    inSentence: {
      hours: plural({ one: "{count} hora", other: "{count} horas" }),
      days: plural({ one: "{count} día", other: "{count} días" }),
      weeks: plural({ one: "{count} semana", other: "{count} semanas" }),
    },
  },

  create: {
    mode: { text: "Texto", file: "Archivo" },
    textPlaceholder: "Pega una contraseña, una clave de API, una clave privada…",

    generator: {
      open: "Generar contraseña",
      close: "Cerrar generador",
      length: "Longitud",
      lowercase: "Minúsculas (a–z)",
      uppercase: "Mayúsculas (A–Z)",
      digits: "Números (0–9)",
      symbols: "Símbolos (!@#…)",
      excludeAmbiguous: "Evitar caracteres parecidos (0/O, 1/l/I)",
      needCharset: "Elige al menos un tipo de carácter.",
      generate: "Generar e insertar",
      note: "Se genera en tu navegador y se inserta donde esté el cursor en el cuadro de abajo, o al final si no has colocado ninguno.",
    },

    dropzone: {
      title: "Suelta un archivo aquí",
      hint: "o {browse} — hasta {size}",
      browse: "examinar",
      note: "cifrado antes de salir de este dispositivo",
      remove: "Quitar",
    },

    encStatus: "Cifrado en tu navegador. La clave vive en el enlace, nunca en nuestros servidores.",

    expiry: {
      label: "Caduca después de",
      custom: "Personalizado",
      unitHours: "Horas",
      unitDays: "Días",
      unitWeeks: "Semanas",
    },

    oneTime: {
      title: "Destruir tras una sola visualización",
      description: "El secreto se borra en el momento en que se abre. Una segunda visita no verá nada.",
    },

    allowedEmail: {
      label: "Restringir a una sola dirección de correo",
      hint: "Todo el que abra el enlace verifica una dirección de correo con un código de un solo uso. Indicar una dirección aquí significa que solo esa persona podrá pasar.",
      placeholder: "solo-esta-persona@ejemplo.com",
    },

    notifyEmail: {
      title: "Avisarme cuando se vea",
      description:
        "Se envía un acuse de lectura cada vez que se abre el secreto, indicando la dirección que lo abrió.",
      label: "Enviar el acuse a",
      placeholder: "tu@ejemplo.com",
    },

    developer: {
      title: "Opciones para desarrolladores",
      webhookUrlLabel: "URL del webhook",
      webhookUrlHint: "Enviamos un evento JSON por POST al crearlo y en cada visualización.",
      webhookSecretLabel: "Secreto de firma del webhook",
      webhookSecretPlaceholder: "al menos 16 caracteres",
      webhookSecretHint:
        "Sirve para firmar la carga útil (HMAC-SHA256, enviada en la cabecera {header}). Obligatorio siempre que se defina una URL de webhook.",
    },

    policy: {
      open: "Este enlace caduca en {expiry}. Cualquiera que tenga el enlace puede abrirlo tras verificar una dirección de correo.",
      openOneTime:
        "Este enlace caduca en {expiry} o en el momento en que se abra, lo que ocurra primero. Cualquiera que tenga el enlace puede abrirlo tras verificar una dirección de correo.",
      restricted: "Este enlace caduca en {expiry}. Solo {email} puede abrirlo.",
      restrictedOneTime:
        "Este enlace caduca en {expiry} o en el momento en que se abra, lo que ocurra primero. Solo {email} puede abrirlo.",
    },

    submit: "Crear enlace secreto",
    submitting: "Cifrando…",

    error: {
      needText: "Escribe el texto que quieres compartir.",
      needFile: "Elige un archivo para compartir.",
      needNotifyEmail: "Escribe una dirección para el acuse de lectura o desactiva el aviso.",
      webhookSecretTooShort: "El secreto de firma del webhook debe tener al menos 16 caracteres.",
      generic: "Algo salió mal al crear el secreto.",
    },

    result: {
      title: "Tu enlace está listo",
      subtitle: "Envíalo como prefieras. Sin el fragmento que va detrás del signo #, el enlace no sirve de nada.",
      linkCaption: "la parte de color arcilla es la clave de descifrado: nunca llega hasta nosotros",
      expires: "Caduca",
      destroyAfterOneView: "Destruir tras una sola visualización",
      restrictedTo: "Restringido a",
      anyVerifiedEmail: "Cualquier correo verificado",
      createAnother: "Crear otro",
    },
  },

  retrieve: {
    invalidLink: {
      title: "Enlace no válido",
      body: "A este enlace le falta su clave de descifrado. Asegúrate de haber copiado la URL entera, incluido todo lo que va después de {hash}.",
      note: "Si has recargado esta página, vuelve a abrir el enlace original: la clave se retira de la barra de direcciones en cuanto carga la página, para que no acabe en tu historial, en una captura de pantalla ni en nada de lo que este sitio informe.",
    },

    notFound: {
      title: "Secreto no encontrado",
      body: "Este secreto ha caducado, ya se ha visto (si era de un solo uso) o nunca existió.",
    },

    alreadyRetrieved: {
      title: "Ya se ha abierto",
      body: "Era un secreto de un solo uso y alguien ya lo ha abierto. No se puede volver a leer: ni por ti, ni por nadie más.",
      note: "Si ese alguien fuiste tú y la descarga no llegó a terminar, prueba el enlace de nuevo en los próximos minutos. Después de eso el secreto desaparece para siempre, y quien te lo envió tendrá que compartirlo otra vez.",
    },

    decrypting: "Descifrando…",

    fatalError: {
      title: "Algo salió mal",
      cannotReachServer: "No se ha podido conectar con el servidor.",
      cannotRequestCode: "No se ha podido solicitar un código de verificación.",
      cannotVerifyCode: "No se ha podido verificar el código.",
      decryptionFailed: "Error al descifrar: puede que el enlace esté dañado o incompleto.",
    },

    email: {
      eyebrow: "Secreto en espera",
      title: "Alguien te ha enviado algo",
      body: "Verifica tu correo para abrirlo. El código es válido durante diez minutos.",
      fileLine: "Archivo: {filename} ({size}). ",
      label: "Tu correo electrónico",
      placeholder: "tu@ejemplo.com",
      submit: "Enviar código",
      submitting: "Enviando…",
      restricted: "Este secreto está restringido a una dirección de correo concreta, y no es esta.",
      locked: "Demasiados intentos incorrectos. Solicita un código nuevo.",
    },

    otp: {
      title: "Introduce tu código",
      body: "Hemos enviado un código de un solo uso a {email}.",
      label: "Código de un solo uso",
      submit: "Desbloquear el secreto",
      submitting: "Verificando…",
      oneTimeNote: "Abrirlo lo destruye. Copia lo que necesites antes de cerrar la pestaña.",
      resend: "Reenviar código",
      resendIn: "Reenviar en {seconds} s",
      incorrect: "Código incorrecto.",
      incorrectWithAttempts: plural({
        one: "Código incorrecto (queda {count} intento).",
        other: "Código incorrecto (quedan {count} intentos).",
      }),
    },

    success: {
      textTitle: "El secreto",
      fileTitle: "Tu archivo",
      destroyedOnServer: "destruido en el servidor",
      clickToReveal: "Haz clic para mostrar",
      sendOneBack: "Enviar uno de vuelta",
      fileDownloaded: "{filename} se ha descifrado en tu navegador y se ha descargado.",
      destroyedNotice:
        "Este secreto se ha destruido en el servidor. Recargar esta página no mostrará nada. Verificado como {email} a las {time}.",
    },
  },

  reply: {
    title: "Envía algo de vuelta",
    intro: "Esto se enviará a {email}, y solo esa persona podrá abrirlo. Puedes enviar una respuesta.",
    receiptTo: "El acuse de lectura se enviará a {email}, la dirección que acabas de verificar.",
    expired: {
      title: "Esta ventana de respuesta se ha cerrado",
      body: "Una respuesta solo se puede enviar desde la página donde abriste un secreto, y solo durante un breve periodo después. Pide a quien te lo envió que comparta un enlace nuevo.",
    },
  },

  auth: {
    signInRequired: "Hay que iniciar sesión para crear un enlace nuevo.",
    expired: {
      title: "Tu sesión ha caducado",
      body: "Se ha cerrado tu sesión porque esta página llevaba un rato abierta. Inicia sesión de nuevo para crear un enlace. Lo que ya habías escrito no se ha conservado.",
    },
    signIn: "Iniciar sesión",
    signedInAs: "Sesión iniciada como {email}",
    signOut: "Cerrar sesión",
  },

  apiError: {
    rateLimited: "Demasiadas solicitudes. Espera un momento y vuelve a intentarlo.",
    tooLarge: "Este archivo es demasiado grande para compartirlo.",
    unauthenticated: "Tu sesión ha caducado. Vuelve a iniciar sesión para crear un enlace.",
    webhookHostNotPermitted: "Esa URL de webhook apunta a un destino al que este servidor no envía solicitudes.",
    webhooksDisabled: "Los webhooks están desactivados en este servidor.",
    validationFailed: "Algunos de los datos de arriba no se han aceptado. Revísalos y vuelve a intentarlo.",
  },
};
